import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Mail, ArrowRight, MapPin, Calendar, Linkedin, Instagram } from "lucide-react";
import PageHero from "@/components/PageHero";
import MagneticButton from "@/components/MagneticButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", revenue_goal: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [subbing, setSubbing] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitContact = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Strategy call request received. We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", company: "", revenue_goal: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) { toast.error("Enter your email first."); return; }
    setSubbing(true);
    try {
      await axios.post(`${API}/newsletter`, { email: newsletterEmail });
      toast.success("You're in. Growth field notes incoming.");
      setNewsletterEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Couldn't subscribe right now.");
    } finally {
      setSubbing(false);
    }
  };

  return (
    <div data-testid="contact-root" className="relative">
      {/* Cinematic background */}
      <div className="absolute inset-x-0 top-0 h-[80vh] -z-10 overflow-hidden">
        <img src="/images/cta.png" alt="" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-bg-0/50 via-bg-0/80 to-bg-0" />
        <div className="absolute inset-0 bg-[radial-gradient(60rem_40rem_at_30%_10%,rgba(255,0,127,0.18),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(50rem_30rem_at_80%_20%,rgba(0,240,255,0.18),transparent_60%)]" />
      </div>

      <PageHero
        eyebrow="Get in touch"
        title={<>Ready to build your <span className="text-gradient-warm">growth system?</span></>}
        subtitle="Tell us a few lines about your business. We&apos;ll come back within 24 hours with a tailored next step."
        accent="warm"
      />

      <section className="container-x pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Form */}
          <form onSubmit={submitContact} className="lg:col-span-7 glass rounded-3xl p-7 sm:p-9" data-testid="contact-form">
            <h2 className="text-2xl font-bold tracking-tight">Book a Strategy Session</h2>
            <p className="mt-2 text-sm text-white/55">45-minute working call. Walk away with a tailored growth blueprint.</p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name *">
                <input data-testid="contact-name" value={form.name} onChange={update("name")} placeholder="Your full name" className="input" />
              </Field>
              <Field label="Email *">
                <input data-testid="contact-email" type="email" value={form.email} onChange={update("email")} placeholder="you@company.com" className="input" />
              </Field>
              <Field label="Company">
                <input data-testid="contact-company" value={form.company} onChange={update("company")} placeholder="Company / Brand" className="input" />
              </Field>
              <Field label="Revenue Goal">
                <select data-testid="contact-revenue" value={form.revenue_goal} onChange={update("revenue_goal")} className="input">
                  <option value="">Select range</option>
                  <option>₹1 – 5 Cr / year</option>
                  <option>₹5 – 25 Cr / year</option>
                  <option>₹25 – 100 Cr / year</option>
                  <option>₹100 Cr+ / year</option>
                </select>
              </Field>
              <div className="sm:col-span-2">
                <Field label="What are you trying to build? *">
                  <textarea data-testid="contact-message" value={form.message} onChange={update("message")} rows={4} placeholder="A few lines about your business and goals" className="input resize-none" />
                </Field>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-white/40">We reply within 24 hours. No spam, ever.</p>
              <MagneticButton type="submit" tone="warm" disabled={submitting} data-testid="contact-submit">
                {submitting ? "Sending…" : "Book Strategy Call"} <Send size={15} />
              </MagneticButton>
            </div>
          </form>

          {/* Side */}
          <aside className="lg:col-span-5 space-y-5">
            <div className="glass rounded-3xl p-7 relative overflow-hidden">
              <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full blur-3xl opacity-30 bg-gradient-to-tr from-neon-cyan to-neon-purple" />
              <Mail className="text-neon-cyan" />
              <h3 className="mt-4 text-xl font-bold tracking-tight">Growth field notes</h3>
              <p className="mt-2 text-sm text-white/55">One short email every two weeks. Real systems, real numbers, zero fluff.</p>
              <form onSubmit={subscribe} className="mt-5 flex gap-2" data-testid="newsletter-form">
                <input data-testid="newsletter-email" type="email" value={newsletterEmail} onChange={(e) => setNewsletterEmail(e.target.value)} placeholder="you@email.com" className="input flex-1" />
                <button type="submit" disabled={subbing} data-testid="newsletter-submit" className="px-4 rounded-xl bg-white text-bg-0 font-semibold text-sm hover:shadow-[0_0_30px_rgba(0,240,255,0.4)] transition disabled:opacity-60">
                  {subbing ? "…" : <ArrowRight size={16} />}
                </button>
              </form>
            </div>

            <div className="glass rounded-3xl p-7">
              <Calendar className="text-neon-magenta" />
              <h3 className="mt-4 text-xl font-bold">Prefer a calendar link?</h3>
              <p className="mt-2 text-sm text-white/55">Calendly integration coming soon. For now, drop us a line and we&apos;ll send time slots.</p>
              <a href="mailto:hello@novariseworks.com" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-neon-cyan hover:underline" data-testid="email-link">
                hello@novariseworks.com <ArrowRight size={14} />
              </a>
            </div>

            <div className="glass rounded-3xl p-7">
              <MapPin className="text-neon-orange" />
              <h3 className="mt-4 text-xl font-bold">Office</h3>
              <p className="mt-2 text-sm text-white/55 leading-relaxed">
                Novarise Works HQ<br />
                Bangalore, India<br />
                Working remote-first across IN / SEA / EMEA.
              </p>
              <div className="mt-5 rounded-xl overflow-hidden border border-white/10 h-36 relative bg-bg-2">
                {/* Map placeholder */}
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(0,240,255,0.25),transparent_60%),radial-gradient(circle_at_70%_60%,rgba(255,0,127,0.25),transparent_60%)]" />
                <div className="absolute inset-0 grid grid-cols-12 grid-rows-6 opacity-20">
                  {Array.from({ length: 72 }).map((_, i) => <div key={i} className="border border-white/10" />)}
                </div>
                <motion.span
                  initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }}
                  className="absolute left-[34%] top-[44%] w-3 h-3 rounded-full bg-neon-cyan shadow-[0_0_20px_rgba(0,240,255,0.7)]"
                />
                <span className="absolute left-[37%] top-[40%] font-mono text-[10px] tracking-[0.18em] uppercase text-white/70">Bangalore</span>
              </div>
            </div>

            <div className="glass rounded-3xl p-7">
              <p className="overline mb-3">Follow along</p>
              <div className="flex items-center gap-2">
                {[
                  { Icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/novariseworks/" },
                  { Icon: Instagram, label: "Instagram", href: "https://www.instagram.com/novariseworks?igsh=MWN6Z29teTVxdDZ5YQ==" },
                ].map(({ Icon, label, href }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    data-testid={`contact-social-${label.toLowerCase()}`}
                    className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:border-white/30 transition"
                  >
                    <Icon size={15} />
                  </a>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <style>{`
        .input {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.10);
          color: #fff;
          padding: 0.7rem 0.9rem;
          border-radius: 0.75rem;
          font-size: 0.92rem;
          transition: all 0.2s ease;
          outline: none;
        }
        .input::placeholder { color: rgba(255,255,255,0.35); }
        .input:focus {
          border-color: rgba(0,240,255,0.55);
          box-shadow: 0 0 0 4px rgba(0,240,255,0.10);
          background: rgba(255,255,255,0.06);
        }
        select.input option { background: #0A0A12; color: #fff; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/45">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
